import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../config';
import './EngagementTicker.css';

export default function EngagementTicker() {
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    // Check localStorage for dismissed state
    const isDismissed = localStorage.getItem('engagementTickerDismissed') === 'true';
    console.log('[TICKER] Initial dismissed state:', isDismissed, 'localStorage value:', localStorage.getItem('engagementTickerDismissed'));
    return isDismissed;
  });
  const tickerRef = useRef(null);

  // Debug: log state changes
  useEffect(() => {
    console.log('[TICKER] Component mounted/updated - dismissed:', dismissed, 'events:', events?.length || 0, 'error:', error);
    if (tickerRef.current) {
      console.log('[TICKER] Ticker element in DOM:', tickerRef.current.offsetHeight, 'px high, visible:', tickerRef.current.offsetHeight > 0);
    }
  }, [dismissed, events, error]);

  useEffect(() => {
    if (dismissed) return;

    const fetchEvents = async () => {
      try {
        console.log('[TICKER] Fetching from:', `${API_BASE_URL}/api/engagement/ticker?limit=20&scan_minutes=60`);
        const response = await fetch(`${API_BASE_URL}/api/engagement/ticker?limit=20&scan_minutes=60`);
        if (!response.ok) throw new Error(`HTTP ${response.status}: Failed to fetch engagement`);
        const data = await response.json();
        console.log('[TICKER] Got events:', data.events?.length || 0);
        setEvents(data.events || []);
        setError(null);
      } catch (error) {
        console.error('[TICKER] Error fetching engagement:', error);
        setError(error.message);
        setEvents([]); // Ensure events is empty array, not undefined
      }
    };

    // Initial fetch
    fetchEvents();

    // Poll every 60 seconds
    const interval = setInterval(fetchEvents, 60000);

    return () => clearInterval(interval);
  }, [dismissed]);

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('engagementTickerDismissed', 'true');
  };

  const handleRecall = () => {
    setDismissed(false);
    localStorage.setItem('engagementTickerDismissed', 'false');
  };

  if (dismissed) {
    return (
      <div className="ticker-recall-bar">
        <button onClick={handleRecall} className="ticker-recall-button">
          📊 Show Engagement Ticker
        </button>
      </div>
    );
  }

  if (!events || events.length === 0) {
    // Show placeholder when no new engagement
    const placeholderText = error ? `Error: ${error}` : 'Waiting for new engagement...';
    return (
      <div className="engagement-ticker">
        <div className="ticker-label">📊 LIVE</div>
        <div className="ticker-scroll-container">
          <div className="ticker-content-static">
            <span className="ticker-item">
              <span className="ticker-event" style={{ opacity: 0.5, color: error ? '#ff6b6b' : '#9bdcff' }}>
                {placeholderText}
              </span>
            </span>
          </div>
        </div>
        <button onClick={handleDismiss} className="ticker-dismiss" title="Dismiss ticker">
          ×
        </button>
      </div>
    );
  }

  const formatEvent = (event) => {
    const icon = event.type === 'like' ? '⚡' : event.type === 'repost' ? '🔁' : '💬';
    const action = event.type === 'like' ? 'liked' : event.type === 'repost' ? 'reposted' : 'replied to';
    
    // Show ONLY the delta (new engagement count)
    const delta = event.delta || 1;
    const deltaText = delta > 1 ? `+${delta}` : '+1';
    
    // Extract first sentence from target text
    let text = event.target_text || '';
    // Find first sentence (ends with . ! or ?)
    const firstSentenceMatch = text.match(/^[^.!?]*[.!?]/);
    if (firstSentenceMatch) {
      text = firstSentenceMatch[0].trim();
    } else {
      // If no sentence ending found, take first ~60 chars
      text = text.substring(0, 60).trim();
    }
    
    const quotedText = text ? `"${text}..."` : '';
    
    return `${icon} ${deltaText} ${action} ${event.target_author}'s ${quotedText}`;
  };

  // Duplicate events for seamless loop
  // If only 1 event, repeat it 8 times for continuous circular scroll
  // If multiple events, duplicate 4 times for truly seamless continuous crawl
  const duplicatedEvents = events.length === 1 
    ? Array(8).fill(events[0])
    : [...events, ...events, ...events, ...events];

  return (
    <div className="engagement-ticker" ref={tickerRef} style={{ background: '#0a1420' }}>
      <div className="ticker-label">
        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>📊 LIVE</div>
        <div style={{ fontSize: '10px', opacity: 0.7, marginTop: '2px' }}>last hour</div>
      </div>
      <div className="ticker-scroll-container">
        <div className="ticker-content">
          {duplicatedEvents.map((event, index) => (
            <a
              key={`${index}-${event.target_author}`}
              href={event.target_url}
              target="_blank"
              rel="noreferrer"
              className="ticker-item"
            >
              <span className="ticker-event">{formatEvent(event)}</span>
            </a>
          ))}
        </div>
      </div>
      <button onClick={handleDismiss} className="ticker-dismiss" title="Dismiss ticker">
        ×
      </button>
    </div>
  );
}

